import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageUp, Save } from 'lucide-react';
import { Button, FormCard, FormField, Skeleton } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { type EntrepriseDTO, JOURS_SEMAINE, getEntreprise, updateEntreprise } from '@/services/parametres';
import { uploadFile } from '@/services/uploads';
import { toast } from '@/store/toastStore';

const FORMES = ['EURL', 'SARL', 'SPA', 'SNC', 'ETS', 'AUTRE'];

type FormValues = Omit<EntrepriseDTO, 'id'>;

export function EntrepriseTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['entreprise'], queryFn: getEntreprise });

  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const logoUrl = watch('logo_url');

  const handleLogo = async (file: File) => {
    setUploading(true);
    try {
      const { url } = await uploadFile(file);
      setValue('logo_url', url, { shouldDirty: true });
      toast.success('Logo téléversé — pensez à enregistrer.');
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: (values: Partial<EntrepriseDTO>) => updateEntreprise(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entreprise'] });
      toast.success('Informations de l\'entreprise enregistrées.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
      <FormCard
        title="Identité légale"
        description="Ces informations figurent dans l'en-tête de tous les documents officiels (factures, situations, bulletins)."
        columns={2}
        footer={
          <Button type="submit" loading={mutation.isPending}>
            <Save /> Enregistrer
          </Button>
        }
      >
        <div className="flex items-center gap-4 sm:col-span-2">
          <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-surface-primary">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="size-full object-contain" />
            ) : (
              <ImageUp className="size-7 text-content-muted" />
            )}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-content-secondary">Logo de l'entreprise</p>
            <p className="mb-2 text-xs text-content-muted">Affiché dans l'en-tête des documents. PNG/JPG.</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogo(f); }}
            />
            <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()} loading={uploading}>
              <ImageUp /> {logoUrl ? 'Changer le logo' : 'Téléverser un logo'}
            </Button>
          </div>
        </div>
        <FormField label="Raison sociale" required className="sm:col-span-2">
          <Input {...register('raison_sociale')} />
        </FormField>
        <FormField label="Forme juridique">
          <select
            {...register('forme_juridique')}
            className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm"
          >
            <option value="">—</option>
            {FORMES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Devise">
          <Input {...register('devise')} />
        </FormField>
        <FormField label="Prix gasoil par défaut (DZD/L)">
          <Input type="number" step="0.01" placeholder="ex : 29,01" {...register('prix_carburant_defaut', { valueAsNumber: true })} />
        </FormField>
        <FormField
          label="Jour de repos hebdomadaire"
          hint="Grisé dans les pointages et les calendriers. N'empêche jamais de pointer une journée réellement travaillée."
        >
          <select
            {...register('jour_repos_hebdo', { valueAsNumber: true })}
            className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm"
          >
            {JOURS_SEMAINE.map((j, i) => (
              <option key={j} value={i}>
                {j}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="NIF (N° Identification Fiscale)">
          <Input {...register('nif')} />
        </FormField>
        <FormField label="NIS (N° Identification Statistique)">
          <Input {...register('nis')} />
        </FormField>
        <FormField label="RC (Registre du Commerce)">
          <Input {...register('rc')} />
        </FormField>
        <FormField label="AI (Article d'Imposition)">
          <Input {...register('ai')} />
        </FormField>
        <FormField label="N° CNAS">
          <Input {...register('cnas_num')} />
        </FormField>
        <FormField label="N° CASNOS">
          <Input {...register('casnos_num')} />
        </FormField>
        <FormField label="Wilaya">
          <Input {...register('wilaya')} />
        </FormField>
        <FormField label="Adresse" className="sm:col-span-2">
          <Input {...register('adresse')} />
        </FormField>
        <FormField label="Téléphone">
          <Input {...register('telephone')} />
        </FormField>
        <FormField label="Email">
          <Input type="email" {...register('email')} />
        </FormField>
      </FormCard>
    </form>
  );
}
